import React, { useState, useEffect, useRef, Fragment } from 'react';

import * as uuid from 'uuid';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import ContentContainer from '../../layout/ContentContainer.jsx';
import request from '@nineeng/request';

import * as authentication from '../../util/authentication.js';
import uploadFile from '../../util/upload.js';
import { DEFAULT_ACCEPT_STRING, getAcceptedExtensions } from '../../util/acceptedFormats.js';
import { DocumentCamera } from '../../components/DocumentCamera.jsx';
import { userNamesExceedTolerance } from '../../util/nameMatching.js';

const explanationStyle = {
	width: '100%',
	padding: '1.5rem',
	boxSizing: 'border-box',
	margin: '0 auto',
	marginTop: '1rem',
	textAlign: 'left',
};

const documentContainerStyle = {
	paddingTop: '1rem',
	width: '100%',
};

const helperUrls = {
	'nl-notification': 'https://www.postedworkers.nl/',
	'nl-gvva': 'https://www.government.nl/topics/foreign-citizens-working-in-the-netherlands/question-and-answer/what-permits-do-foreign-workers-need',
	'nl-twv': 'https://www.government.nl/topics/foreign-citizens-working-in-the-netherlands/question-and-answer/what-permits-do-foreign-workers-need',
};

const DocUploadLine = ({
	index,
	docTypeId,
	allowedTypes,
	value,
	label,
	name,
	uploadUrl,
	onChange, 
	required,
	text,
	useCamera,
	cameraIncludes,
	cameraRotationAngle,
	cameraZoom,
	useDocumentDetection,
	minValidityPeriod,
	disabled,
	helperText,
	allowedFormats,
	docTypes,
	context,
	validateNames = false,
	continueWithError = false,
	addPendingAction = () => { },
	removePendingAction = () => { },
}) => {
	const [hasError, setError] = useState(null);
	const [hasFeedback, setFeedback] = useState(null);
	const [isLoading, setLoading] = useState(false);
	const [image, setImage] = useState();
	const [showCamera, setShowCamera] = useState(false);
	const [erroredFile, setErroredFile] = useState(null);
	const [showErrorAcceptance, setShowErrorAcceptance] = useState(false);
	const [isErrorExpanded, setIsErrorExpanded] = useState(false);
	const [isNameValidationError, setIsNameValidationError] = useState(false);

	const fileInputRef = useRef(null);

	const blobToCanvas = async (blob) => {
		return new Promise((resolve) => {
			const canvas = document.createElement('canvas');
			var ctx = canvas.getContext('2d');
			var img = new Image();
			img.onload = function () {
				canvas.width = img.width;
				canvas.height = img.height;
				ctx.drawImage(img, 0, 0);
				resolve(canvas);

			};
			img.src = URL.createObjectURL(blob);
		});
	};


	const handleFileUpload = async (e) => {
		const file = e.target.files[0];
		await processFileUpload(file, file.type);
	};

	const setErrorMessage = (errorMessage) => {
		return setError(errorMessage);
	};

	const setErrorWithContinueOption = (errorMessage, uploadedFile) => {
		if (continueWithError && uploadedFile) {
			setErroredFile(uploadedFile);
			setShowErrorAcceptance(true);
			setIsNameValidationError(true);
		}
		return setError(errorMessage);
	};

	const processFileUpload = async (file, type) => {
		if (isLoading) {
			return;
		}
		setLoading(true);
		setError(null);
		setFeedback(null);
		const action = uuid.v4();
		try {
			addPendingAction(action);

			const uploadParams = {
				contentType: type,
				type: docTypeId || 'generic',
				filename: label,
				context,
				allowedDocumentTypes: allowedTypes,
			};

			const { faceImageBase64, ...uploadedFile } = await uploadFile(uploadUrl, file, false, uploadParams);
			
			// File comes from ai-api, check if the document is valid, it comes with its own feedback
			if (uploadedFile?.metadata?.validity === false) {
				const feedbackError = uploadedFile?.metadata?.validityFeedback || 'Document validation failed';
				return setErrorMessage(feedbackError);
			}

			// Handle multi-type validation
			if (allowedTypes) {

				if (docTypeId === 'any-id' && uploadedFile.status === 'inReview' && uploadedFile.reviewReason.includes('UNSUPPORTED_CLASS')) {
					return setErrorMessage(text.errorUnsupportedClass);
				}

				// Check if returned type is in allowed list
				if (!allowedTypes.includes(uploadedFile.type)) {
					const formattedError = text.errorWrongType
						.replace('{expectedType}', allowedTypes.join(' or '))
						.replace('{uploadedFileType}', uploadedFile.type);
					
					return setErrorMessage(formattedError);
				}
			} else {
				if (docTypeId !== 'any-id' && uploadedFile?.type !== 'generic' && uploadedFile.type !== docTypeId) {
					const expectedTypeLabel = docTypeId;
					
					// Format error message with both types
					const formattedError = text.errorWrongType
						.replace('{expectedType}', expectedTypeLabel)
						.replace('{uploadedFileType}', uploadedFile.type);
					
					return setErrorMessage(formattedError);
				}

				if (docTypeId === 'any-id' && !['id', 'passport'].includes(uploadedFile.type)) {
					const formattedError = text.errorWrongType.replace('{documentType}', label || docTypeId);
					return setErrorMessage(formattedError);
				}
			}

			const maxAcceptedExpiryPeriodDate = minValidityPeriod ? new Date(Date.now() + minValidityPeriod * 24 * 60 * 60 * 1000) : new Date();
			if (uploadedFile?.metadata?.expiryDate && new Date(uploadedFile?.metadata?.expiryDate) < maxAcceptedExpiryPeriodDate) {
				return setErrorMessage(text.expiredError);
			}

			if(validateNames) {
				if (context.firstName &&context.lastName) {

					if (!uploadedFile.metadata.firstName || !uploadedFile.metadata.lastName) {
						return setErrorWithContinueOption(text.errorNameNotFound || 'Name is not found on document', uploadedFile);
					}

					if(userNamesExceedTolerance(context, uploadedFile.metadata)) {
						uploadedFile.status = 'inReview';
						return setErrorWithContinueOption(text.errorNameMismatch || 'Name of the user performing the task does not match the name on the document', uploadedFile);
					}
				} 
			}
			
			if (faceImageBase64) setImage(`data:image/png;base64,${faceImageBase64}`);
			if (uploadedFile.status === 'inReview') setFeedback(text.feedbackReview);
			
			return onChange(index, uploadedFile);
		} catch (err) {
			console.error(err);
			onChange(index, null);
			setError(err);
		} finally {
			removePendingAction(action);
			setLoading(false);
		}
	};

	const handleClick = (() => {
		if (useCamera) {
			setShowCamera(true);
			return;
		}
		if (!fileInputRef.current) return;
		fileInputRef.current.click();
	});

	const handleToggleErrorExpansion = () => {
		setIsErrorExpanded(!isErrorExpanded);
	};

	const handleAcceptError = () => {
		if (erroredFile) {
			// Mark the file as accepted with error and set status to inReview
			const fileWithReviewStatus = {
				...erroredFile,
				status: 'inReview',
			};
			
			onChange(index, fileWithReviewStatus);
			setShowErrorAcceptance(false);
			setIsErrorExpanded(false);
			// Keep the error visible but allow continuation
		}
	};

	const handleCancelError = () => {
		setShowErrorAcceptance(false);
		setErroredFile(null);
		setError(null);
		setIsErrorExpanded(false);
		setIsNameValidationError(false);
		onChange(index, null);
	};

	const acceptString = allowedFormats ?? DEFAULT_ACCEPT_STRING;
	const acceptedFormats = getAcceptedExtensions(acceptString);

	const FileInput = () => <input ref={fileInputRef} accept={acceptString} style={{ display: 'none' }} type='file' onChange={handleFileUpload} />;

	const {
		countryName = '',
		firstName = '',
		lastName = '',
		dateOfExpiry = '',
	} = value?.metadata || {};



	if (value && !isLoading) return (
		<div style={{ marginBottom: '1rem' }}>
			<Card variant="outlined" sx={{ '& p': { margin: 0 } }}>
				{image && <img style={{ position: 'absolute', height: 100, borderRadius: 4 }} src={image} />}
				<List disablePadding>
					<ListItem
						button
						onClick={handleClick}
						disabled={disabled}
						sx={{ height: 100 }}
					>
						{!value.faceImage && <ListItemIcon sx={{ minWidth: 80 }}>
							<CheckIcon />
						</ListItemIcon>}
						<ListItemText
							sx={{ paddingLeft: value.faceImage ? '80px' : 0 }}
							primary={`${label} ${required ? '*' : ''}`}
							primaryTypographyProps={{ color: 'green' }}
							secondary={name && <Fragment>
								<p>{firstName} {lastName}</p>
								{countryName && <p>{text.lblCountry}: {countryName}</p>}
								{dateOfExpiry && <p>{text.lblExpires}: {new Date(dateOfExpiry).toLocaleDateString()}</p>}
								{hasFeedback}
							</Fragment>}
						/>
					</ListItem>
				</List>
			</Card>
			{helperText && helperUrls[docTypeId] && (
				<Typography variant='caption' sx={{ marginTop: 2, cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '100%', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
					{helperText + ': '}
					<Link sx={{ color: 'inherit', '&:hover': { color: 'rgba(0, 0, 0, 0.6)' } }} href={helperUrls[docTypeId]} target='_blank'>{helperUrls[docTypeId]}</Link>
				</Typography>)}
			{
				useCamera ?
					showCamera && <DocumentCamera
						title={label}
						cameraIncludes={cameraIncludes}
						cameraRotationAngle={cameraRotationAngle}
						cameraZoom={cameraZoom}
						onClose={() => setShowCamera(false)}
						onLoading={() => setLoading(true)}
						onImage={handleFileUpload}
					/>
					:
					<FileInput />
			}
		</div>
	);

	return (
		<div style={{ marginBottom: '1rem' }}>
			<Card variant="outlined">
				<List disablePadding>
					<ListItem
						button
						onClick={handleClick}
						disabled={isLoading || disabled}
						sx={{ minHeight: 100 }}
					>
						<ListItemIcon sx={{ minWidth: 80 }}>
							{isLoading ? <CircularProgress /> : <FileUploadIcon />}
						</ListItemIcon>
						<ListItemText
							primary={`${label} ${required ? '*' : ''}`}
							secondary={hasError
								|| (acceptedFormats.length > 0
									? `${text.lblUploadOrCapture} (${acceptedFormats.join(', ')})`
									: text.lblUploadOrCapture)}
							secondaryTypographyProps={
								hasError
									? { color: 'red', sx: { whiteSpace: 'pre-line' } }
									: {}
							}
						/>
					</ListItem>
					
					{/* Error dropdown trigger - shown when continueWithError is true, there's an error, and it's a name validation error */}
					{showErrorAcceptance && continueWithError && hasError && isNameValidationError && (
						<>
							<Divider />
							<ListItem
								button
								onClick={handleToggleErrorExpansion}
								sx={{ 
									backgroundColor: 'rgba(244, 67, 54, 0.08)',
									'&:hover': {
										backgroundColor: 'rgba(244, 67, 54, 0.12)',
									},
								}}
							>
								<ListItemText
									primary={text.errorDropdownTitle || 'I got an error, what does this mean?'}
									primaryTypographyProps={{
										variant: 'body2',
										color: 'error.main',
										fontWeight: 500,
									}}
								/>
								<ListItemIcon sx={{ minWidth: 'auto' }}>
									{isErrorExpanded ? <ExpandLessIcon color="error" /> : <ExpandMoreIcon color="error" />}
								</ListItemIcon>
							</ListItem>
							
							<Collapse in={isErrorExpanded} timeout="auto" unmountOnExit>
								<Box sx={{ padding: 2, backgroundColor: 'rgba(244, 67, 54, 0.04)' }}>
									<Typography variant="body2" sx={{ marginBottom: 2, color: 'text.secondary', textAlign: 'left' }}>
										{text.continueWithErrorExplainer || 'We were not able to process your file. In case you think you uploaded the right file, you can click the continue button, but this will put your user in review and will not give you access without being reviewed first.'}
									</Typography>
									<Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
										<Button 
											variant="outlined" 
											size="small"
											onClick={handleCancelError}
										>
											{text.cancelErrorButton || 'Cancel'}
										</Button>
										<Button 
											variant="contained" 
											color="error" 
											size="small"
											onClick={handleAcceptError}
										>
											{text.acceptErrorButton || 'Continue Anyway'}
										</Button>
									</Box>
								</Box>
							</Collapse>
						</>
					)}
				</List>
			</Card>
			
			{helperText && helperUrls[docTypeId] && (
				<Typography variant='caption' sx={{ marginTop: 1, cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '100%', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
					{helperText + ': '}
					<Link sx={{ color: 'inherit', '&:hover': { color: 'rgba(0, 0, 0, 0.6)' } }} href={helperUrls[docTypeId]} target='_blank'>{helperUrls[docTypeId]}</Link>
				</Typography>
			)}
			{
				useCamera ?
					showCamera && <DocumentCamera
						title={label}
						cameraIncludes={cameraIncludes}
						cameraRotationAngle={cameraRotationAngle}
						cameraZoom={cameraZoom}
						onClose={() => setShowCamera(false)}
						onImage={handleFileUpload}
						onLoading={() => setLoading(true)}
					/>
					:
					<FileInput />
			}
		</div>
	);
};

const ExtendedScanningPage = ({
	possibleDocuments = [],
	requiredDocuments = [],
	uploadUrl,
	onChange,
	addPendingAction,
	removePendingAction,
	minValidityPeriod,
	value = [],
	labels,
	helperTexts,
	text,
	explainer,
	handleRequestException,
	exceptionText,
	useCamera,
	cameraIncludes,
	cameraRotationAngle,
	cameraZoom = 1,
	useDocumentDetection,
	disabled,
	helperText,
	context,
	documentTypeForMultipleTypes,
	validateNames = false,
	continueWithError = false,
}) => {
	const [documentTypes, setDocumentTypes] = useState();
	const [loading, setLoading] = useState(true);
	useEffect(() => {
		const fetchDocumentTypes = async () => {
			try {
				const authToken = authentication.extractAuthorization();
				const authParams = authentication.extractAuthParams();
				const { body } = await request.get(`/api/documentTypes?pagesize=0&${authParams}`, {
					headers: { Authorization: authToken },
				});
				if (body) {
					setDocumentTypes(body.data ?? []);
				} else {
					setDocumentTypes([]);
				}
			} catch (err) {
				setDocumentTypes([]);
			} finally {
				setLoading(false);
			}
		};

		if (!documentTypes) {
			fetchDocumentTypes();
		}
	}, []);

	const docMapper = (doc, required) => {
		// Handle multi-type documents (array of document types)
		if (Array.isArray(doc)) {
			const allowedTypes = doc;
			const compositeId = doc.join(','); // e.g., 'residence-permit,bijlage-3'

			if (!documentTypeForMultipleTypes) {
				console.error('documentTypeForMultipleTypes is not set');
				return;
			}

			// As this is a multi-type document, we need the unknown-user-document-type
			const documentType = documentTypes?.find((docType) => docType.id === documentTypeForMultipleTypes);

			return {
				id: documentType?.id,
				allowedTypes, // Store the array for validation
				compositeId,
				required,
				label: labels[compositeId] || doc.join(' or '),
				allowedFormats: documentType?.allowedFormats,
				parsingMethod: documentType?.parsingMethod,
			};
		}
		
		const documentType = documentTypes?.find((docType) => docType.id === doc);
		return {
			id: doc,
			compositeId: doc,
			required,
			label: documentType?.name,
			allowedFormats: documentType?.allowedFormats,
			parsingMethod: documentType?.parsingMethod,
		};
	};

	const docs = [
		...requiredDocuments.map((doc) => docMapper(doc, true)),
		...possibleDocuments.filter(possibleDoc =>
			!requiredDocuments.some(requiredDoc => possibleDoc === requiredDoc))
			.map((doc) => docMapper(doc, false)),
	];

	return (
		<ContentContainer innerStyle={{ padding: 0, width: '100%' }}>
			<div style={explanationStyle}>
				{explainer}
				<div style={documentContainerStyle}>
					{docs.map((doc, i) => (
						<>
							<DocUploadLine
								index={i}
								docTypeId={doc.id}
								allowedTypes={doc.allowedTypes}
								key={doc.id}
								minValidityPeriod={minValidityPeriod}
								onChange={onChange}
								uploadUrl={uploadUrl}
								addPendingAction={addPendingAction}
								removePendingAction={removePendingAction}
								value={value[i]}
								label={labels[doc.id] || doc.label || doc.id}
								name={doc.label}
								required={doc.required}
								text={text}
								// useCamera only takes effect when a device header is present (deviceId= in the URL);
								// on regular (non-kiosk) sessions it has no impact.
								useCamera={useCamera && window.location.href.includes('deviceId=')}
								cameraIncludes={cameraIncludes}
								cameraRotationAngle={cameraRotationAngle}
								cameraZoom={cameraZoom}
								useDocumentDetection={useDocumentDetection}
								disabled={disabled || loading}
								helperText={helperText}
								allowedFormats={doc.allowedFormats}
								parsingMethod={doc.parsingMethod}
								docTypes={documentTypes}
								context={context}
								validateNames={validateNames}
								continueWithError={continueWithError}
							/>
							{doc.compositeId && helperTexts?.[doc.compositeId] && (
								<Typography 
									variant='caption' 
									sx={{ marginBottom: 2, marginLeft: '2rem', marginTop: 1, display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
									{helperTexts[doc.compositeId]}
								</Typography>
							)}
						</>
					))}
				</div>
			</div>
			{handleRequestException && 
				(docs.flatMap(document => document.id).includes('limosa') || docs.flatMap(document => document.id).includes('proof-of-independence')) && 
					<Typography 
						variant='caption' 
						sx={{ marginBottom: 2, textDecoration: 'underline', cursor: 'pointer', marginLeft: '2rem' }} 
						onClick={handleRequestException}>
						{exceptionText || text.requestException}
					</Typography>
			}
		</ContentContainer>
	);
};

export default ExtendedScanningPage;
